import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { SankeyAuto } from "../charts-motion/sankey";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_finance_fig_AID_FLOWS";
import { datasets } from "../data/data_finance_fig_AID_FLOWS_datasets";
import { type ChartRenderer, getAidFlowName, mkGemDataDecoder } from "../domain";
import * as M from "../materials";
import { io } from "../prelude";

export * from "../data/data_finance_fig_AID_FLOWS";

export const Data = mkGemDataDecoder([], {
  donor: io.string,
  flow: io.number,
  recipient: io.string,
  level: io.string,
});
export type Data = io.TypeOf<typeof Data>;
export type NodeGroup<D extends Record<string, any>> = {
  nodes: keyof D;
  depth: number;
  label: string;
  category: string;
};

export const Chart = ({ data }: ChartRenderer<Data>) => {
  const { aidflows } = useConfig();
  const intl = useIntl();

  const nodeGroups: NodeGroup<Data[number]>[] = React.useMemo(
    () => [
      {
        nodes: "donor",
        depth: 0,
        label: intl.formatMessage(defineMessage({ id: "Donors", defaultMessage: "Donors" })),
        category: "donors",
      },
      {
        nodes: "recipient",
        depth: 1,
        label: intl.formatMessage(defineMessage({ id: "Recipients", defaultMessage: "Recipients" })),
        category: "recipients",
      },
      {
        nodes: "level",
        depth: 2,
        label: intl.formatMessage(defineMessage({ id: "Education levels", defaultMessage: "Education levels" })),
        category: "education_levels",
      },
    ],
    [intl],
  );

  const colorPalettes = React.useMemo(
    () => [
      {
        label: intl.formatMessage(defineMessage({ id: "Donors", defaultMessage: "Donors" })),
        palette: M.q06Palette,
        id: "donors",
      },
      {
        label: intl.formatMessage(defineMessage({ id: "Recipients", defaultMessage: "Recipients" })),
        palette: M.q03Palette,
        id: "recipients",
      },
      {
        label: intl.formatMessage(defineMessage({ id: "Education levels", defaultMessage: "Education levels" })),
        palette: M.q10Palette,
        id: "education_levels",
      },
    ],
    [intl],
  );

  const sankeyData = React.useMemo(() => {
    const localizedData = data.map((row) => ({
      ...row,
      donorId: row.donor,
      donor: getAidFlowName(aidflows, "donor", row.donor),
      recipientId: row.recipient,
      recipient: getAidFlowName(aidflows, "recipient", row.recipient),
      levelId: row.level,
      level: getAidFlowName(aidflows, "level", row.level),
    }));
    return computeSankeyNodes(localizedData, nodeGroups);
  }, [aidflows, data, nodeGroups]);

  return (
    <SankeyAuto data={sankeyData} height={M.chartHeight.l} colorPalettes={colorPalettes} nodeGroups={nodeGroups} />
  );
};

export default withFigureIO({
  url: new URL("../data/data_finance_fig_AID_FLOWS.json", import.meta.url).href,
  csv: new URL("../data/data_finance_fig_AID_FLOWS.zip", import.meta.url).href,
  xlsx: new URL("../data/data_finance_fig_AID_FLOWS.xlsx", import.meta.url).href,
  metadata,
  Data,
  Chart,
  datasets,
});

// -----------------------------------------------------------------------------

export function computeSankeyNodes<D extends { flow: number }>(data: D[], nodeGroups: NodeGroup<D>[]) {
  const nodes = Array.from(new Set(nodeGroups.flatMap((group) => data.map((x) => x[group.nodes]))).values()).map(
    (id) => ({ id: id as $FixMe }),
  );

  const links = nodeGroups.flatMap((source) => {
    const target = nodeGroups.find((g) => g.depth === source.depth + 1);
    if (!target) return [];
    return data.map((x, linkId) => ({
      source: x[source.nodes],
      target: x[target.nodes],
      value: x.flow,
      linkId,
    }));
  });

  return { nodes, links };
}
